import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Archive, X } from "lucide-react";
import { api, type PlannerClusterNode, type PlannerPageType, type ClusterFamily, type ClusterIntentLabel, type Priority } from "../../lib/api.js";

const FAMILIES: ClusterFamily[] = ["MTH", "LOC", "COND", "REM", "NUT", "WELL", "LONG", "PRO", "BRAND"];
const INTENTS: ClusterIntentLabel[] = ["T", "C", "I", "B"];
const PAGE_TYPES: PlannerPageType[] = ["hub", "landing", "editorial", "comparison", "faq_section", "city_method", "pro_article"];
const PRIORITIES: Priority[] = ["low", "medium", "high", "urgent"];

/**
 * Edit / move / archive a cluster. The taxonomy structure was seeded-once and
 * otherwise read-only; this lets the operator reshape it as they upload keywords —
 * rename, re-classify (pageType/intent), re-parent (move under a different page or
 * family), or archive (soft-delete, children re-parent up).
 */
export function ClusterEditModal({
  cluster,
  allClusters,
  onClose,
  onSaved,
}: {
  cluster: PlannerClusterNode;
  allClusters: PlannerClusterNode[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [nameHe, setNameHe] = useState(cluster.nameHe);
  const [nameEn, setNameEn] = useState(cluster.nameEn);
  const [description, setDescription] = useState(cluster.description ?? "");
  const [businessPriority, setBusinessPriority] = useState<Priority>((cluster.businessPriority as Priority) ?? "medium");
  const [intentLabel, setIntentLabel] = useState<ClusterIntentLabel | "">(cluster.intentLabel ?? "");
  const [pageType, setPageType] = useState<PlannerPageType | "">(cluster.pageType ?? "");
  const [phase, setPhase] = useState<string>(cluster.phase ? String(cluster.phase) : "");
  const [supplyGate, setSupplyGate] = useState<string>(cluster.supplyGate != null ? String(cluster.supplyGate) : "");
  // Move
  const [family, setFamily] = useState<ClusterFamily>(cluster.family as ClusterFamily);
  const [parentClusterId, setParentClusterId] = useState<string>(cluster.parentClusterId ?? "");
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["planner", "tree"] }); onSaved(); };

  const parentOptions = useMemo(
    () => allClusters.filter((c) => c.id !== cluster.id).sort((a, b) => `${a.family}${a.nameHe}`.localeCompare(`${b.family}${b.nameHe}`)),
    [allClusters, cluster.id],
  );

  const save = useMutation({
    mutationFn: () => api.patchCluster(cluster.id, {
      nameHe, nameEn,
      description: description.trim() || null,
      businessPriority,
      intentLabel: intentLabel || null,
      pageType: pageType || null,
      phase: phase ? (Number(phase) as 1 | 2 | 3) : null,
      supplyGate: supplyGate.trim() === "" ? null : Number(supplyGate),
    }),
    onSuccess: (r) => { if (!r.ok) { setBanner({ ok: false, text: r.error ?? "Save failed" }); return; } setBanner({ ok: true, text: "Saved." }); invalidate(); },
    onError: (e: Error) => setBanner({ ok: false, text: e.message }),
  });

  const move = useMutation({
    mutationFn: () => api.reparentCluster(cluster.id, { parentClusterId: parentClusterId || null, family }),
    onSuccess: (r) => { if (!r.ok) { setBanner({ ok: false, text: r.error ?? "Move failed" }); return; } setBanner({ ok: true, text: "Moved." }); invalidate(); },
    onError: (e: Error) => setBanner({ ok: false, text: e.message }),
  });

  const archive = useMutation({
    mutationFn: () => api.archiveCluster(cluster.id),
    onSuccess: (r) => { if (!r.ok) { setBanner({ ok: false, text: r.error ?? "Archive failed" }); return; } invalidate(); onClose(); },
    onError: (e: Error) => setBanner({ ok: false, text: e.message }),
  });

  const busy = save.isPending || move.isPending || archive.isPending;

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-ink/30 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div className="fixed left-1/2 top-1/2 z-[61] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-5 shadow-2xl">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold">Edit cluster</div>
            <div className="truncate text-2xs text-muted" dir="auto">{cluster.id}</div>
          </div>
          <button onClick={onClose} className="btn-ghost shrink-0"><X className="h-4 w-4" /></button>
        </div>

        <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1 text-xs">
          {/* Names */}
          <div className="grid grid-cols-2 gap-2">
            <Field label="Hebrew name"><input dir="rtl" className="input w-full" value={nameHe} onChange={(e) => setNameHe(e.target.value)} /></Field>
            <Field label="English name"><input className="input w-full" value={nameEn} onChange={(e) => setNameEn(e.target.value)} /></Field>
          </div>
          {/* Classification */}
          <div className="grid grid-cols-3 gap-2">
            <Field label="Intent">
              <select className="input w-full" value={intentLabel} onChange={(e) => setIntentLabel(e.target.value as ClusterIntentLabel | "")}>
                <option value="">—</option>
                {INTENTS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </Field>
            <Field label="Page type">
              <select className="input w-full" value={pageType} onChange={(e) => setPageType(e.target.value as PlannerPageType | "")}>
                <option value="">—</option>
                {PAGE_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Priority">
              <select className="input w-full" value={businessPriority} onChange={(e) => setBusinessPriority(e.target.value as Priority)}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Phase (1–3)"><input className="input w-full" value={phase} onChange={(e) => setPhase(e.target.value)} placeholder="—" /></Field>
            <Field label="Supply gate (# therapists)"><input className="input w-full" value={supplyGate} onChange={(e) => setSupplyGate(e.target.value)} placeholder="—" /></Field>
          </div>
          <Field label="Description"><textarea className="input w-full" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
          <div className="flex justify-end">
            <button className="btn-primary text-xs" disabled={busy} onClick={() => save.mutate()}>
              {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save changes
            </button>
          </div>

          {/* Move */}
          <div className="mt-1 rounded-lg border border-line p-2.5">
            <div className="mb-2 text-xs font-semibold">Move in taxonomy</div>
            <div className="grid grid-cols-1 gap-2">
              <Field label="Family">
                <select className="input w-full" value={family} onChange={(e) => setFamily(e.target.value as ClusterFamily)}>
                  {FAMILIES.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </Field>
              <Field label="Parent cluster (the page this belongs under)">
                <select className="input w-full" value={parentClusterId} onChange={(e) => setParentClusterId(e.target.value)}>
                  <option value="">— none (top-level) —</option>
                  {parentOptions.map((c) => (
                    <option key={c.id} value={c.id}>{c.family}{c.subFamily ? `/${c.subFamily}` : ""} — {c.nameHe} ({c.id})</option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="mt-2 flex justify-end">
              <button className="btn-secondary text-xs" disabled={busy} onClick={() => move.mutate()}>
                {move.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Move
              </button>
            </div>
          </div>

          {/* Archive */}
          <div className="mt-1 flex items-center justify-between rounded-lg border border-danger/30 bg-danger-soft/40 p-2.5">
            <div className="text-2xs text-muted">Archiving hides this cluster; its children re-parent up. Reversible.</div>
            <button
              className="btn-ghost text-xs text-danger"
              disabled={busy}
              onClick={() => { if (window.confirm(`Archive "${cluster.nameHe}"? Its child clusters re-parent up. This is reversible.`)) archive.mutate(); }}>
              {archive.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />} Archive
            </button>
          </div>

          {banner && <div className={`rounded-md px-2.5 py-1.5 text-2xs ${banner.ok ? "bg-accent/10 text-accent" : "bg-danger/10 text-danger"}`} dir="auto">{banner.text}</div>}
        </div>
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-0.5 text-2xs text-muted">{label}</div>
      {children}
    </label>
  );
}
